import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { X, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { UserCapabilities, UserContext, DashboardMetrics } from '../utils/dashboardTypes';
import { getIcon } from '../utils/iconMapping';
import { dismissCTA, isDismissed, getDismissedCTAs } from '../utils/ctaDismissal';
import { createCTADefinitions, selectCTAsToDisplay } from '../utils/onboardingCtas';

interface OnboardingViewProps {
  userContext: UserContext;
  metrics: DashboardMetrics;
  capabilities: UserCapabilities;
  userProfile: any;
  onAddRole: () => void;
  onAddCandidate: () => void;
  onConnectBullhorn: () => void;
  onConnectJobAdder: () => void;
  onCompleteProfile?: () => void;
  onSetPreferences?: () => void;
  onExploreMarketplace?: () => void;
  onTakeTour?: () => void;
  onViewHelp?: () => void;
}

export function OnboardingView({
  userContext,
  metrics,
  capabilities,
  userProfile,
  onAddRole,
  onAddCandidate,
  onConnectBullhorn,
  onConnectJobAdder,
  onCompleteProfile,
  onSetPreferences,
  onExploreMarketplace,
  onTakeTour,
  onViewHelp,
}: OnboardingViewProps) {
  const companyName = userProfile?.company?.name || 'Role Relay';
  const firstName = userProfile?.firstName || '';
  const [dismissedCTAs, setDismissedCTAs] = useState<Record<string, any>>({});
  const [dismissingCTAs, setDismissingCTAs] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load dismissed CTAs on mount with initial loading state
  useEffect(() => {
    const dismissed = getDismissedCTAs();
    setDismissedCTAs(dismissed);

    // Show skeleton loader briefly for smooth UX
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 400);

    return () => clearTimeout(timer);
  }, []);

  // Handle CTA dismissal with animation
  const handleDismiss = (ctaId: string, type: 'session' | 'temporary' | 'permanent') => {
    // Start dismiss animation
    setDismissingCTAs([...dismissingCTAs, ctaId]);

    // Wait for animation to complete
    setTimeout(() => {
      dismissCTA(ctaId, type);
      const dismissed = getDismissedCTAs();
      setDismissedCTAs(dismissed);
      setDismissingCTAs(dismissingCTAs.filter(id => id !== ctaId));
    }, 300);
  };

  // Contact admin handler
  const handleContactAdmin = () => {
    const adminEmail = userProfile?.company?.adminEmail || 'admin@yourcompany.com';
    const subject = encodeURIComponent('Request: ATS Connection Access');
    const body = encodeURIComponent(
      `Hi,\n\nI'd like to request access to connect our ATS system to Role Relay. ` +
      `This would help me sync roles and candidates automatically.\n\nThanks!`
    );
    window.location.href = `mailto:${adminEmail}?subject=${subject}&body=${body}`;
  };

  // Create CTA definitions with callbacks
  const ctaDefinitions = useMemo(
    () =>
      createCTADefinitions({
        onConnectBullhorn,
        onConnectJobAdder,
        onAddRole,
        onAddCandidate,
        onCompleteProfile,
        onSetPreferences,
        onExploreMarketplace,
        onTakeTour,
        onViewHelp,
      }),
    [onConnectBullhorn, onConnectJobAdder, onAddRole, onAddCandidate, onCompleteProfile, onSetPreferences, onExploreMarketplace, onTakeTour, onViewHelp]
  );

  // Select CTAs to display (max 3)
  const activeCTAs = useMemo(
    () => selectCTAsToDisplay(userContext, metrics, ctaDefinitions, 3),
    [userContext, metrics, ctaDefinitions]
  );


  // Skeleton loader component
  const CTASkeleton = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className={cn(
            "rounded-xl border border-slate-200 dark:border-slate-700",
            "bg-white dark:bg-slate-800/50",
            "p-8 animate-pulse"
          )}
        >
          <div className="w-12 h-12 rounded-lg bg-slate-200 dark:bg-slate-700 mb-5" />
          <div className="h-7 w-3/4 bg-slate-200 dark:bg-slate-700 rounded mb-4" />
          <div className="h-5 w-full bg-slate-100 dark:bg-slate-700/50 rounded mb-2" />
          <div className="h-5 w-2/3 bg-slate-100 dark:bg-slate-700/50 rounded mb-6" />
          <div className="h-11 w-36 bg-slate-200 dark:bg-slate-700 rounded" />
        </div>
      ))}
    </div>
  );

  return (
    <div>
      {/* Welcome Header - Full width with gradient */}
      <header className={cn(
        "text-center",
        "rounded-xl",
        "bg-gradient-to-r from-teal-600 via-teal-700 to-teal-800",
        "dark:from-teal-700 dark:via-teal-800 dark:to-teal-900",
        "px-8 py-12 mb-10"
      )}>
        <h1 className={cn(
          "font-serif text-4xl lg:text-5xl font-bold",
          "text-white",
          "mb-3"
        )}>
          Welcome to {companyName}
          {firstName && <span className="text-teal-200">, {firstName}</span>}!
        </h1>
        <p className="text-xl text-teal-100/90">
          Let's get you set up. Here's what you can do to get started.
        </p>
      </header>

      {/* CTA Cards Grid - Full width, larger cards */}
      {isLoading ? (
        <CTASkeleton />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-8">
          {activeCTAs.map((cta, index) => {
            const IconComponent = getIcon(cta.icon);
            const isDismissing = dismissingCTAs.includes(cta.id);
            const isPrimary = cta.type === 'primary';

            return (
              <div
                key={cta.id}
                className={cn(
                  "group relative",
                  "rounded-xl",
                  "border-2",
                  isPrimary
                    ? "border-teal-500 dark:border-teal-600"
                    : "border-slate-200 dark:border-slate-700",
                  "bg-white dark:bg-slate-900",
                  "shadow-sm",
                  "transition-all duration-200",
                  "hover:shadow-lg",
                  isPrimary
                    ? "hover:border-teal-600 dark:hover:border-teal-500"
                    : "hover:border-teal-200 dark:hover:border-teal-700",
                  // Primary background tint
                  isPrimary && "bg-gradient-to-br from-teal-50/50 to-white dark:from-teal-950/30 dark:to-slate-900",
                  // Dismissing animation
                  isDismissing && "opacity-0 scale-95 pointer-events-none",
                  // Stagger animation
                  "animate-in fade-in slide-in-from-bottom-4",
                  "fill-mode-both"
                )}
                style={{ animationDelay: `${index * 100}ms`, animationDuration: '400ms' }}
              >
                {/* Dismiss button */}
                <button
                  onClick={() => handleDismiss(cta.id, cta.dismissalType || 'session')}
                  className={cn(
                    "absolute top-4 right-4 z-10",
                    "p-2 rounded-full",
                    "text-slate-400 hover:text-slate-600",
                    "dark:text-slate-500 dark:hover:text-slate-300",
                    "hover:bg-slate-100 dark:hover:bg-slate-800",
                    "transition-all duration-200",
                    "opacity-0 group-hover:opacity-100",
                    "focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                  )}
                  aria-label="Dismiss"
                  title={
                    cta.dismissalType === 'permanent'
                      ? "Don't show this again"
                      : cta.dismissalType === 'temporary'
                      ? 'Dismiss for 7 days'
                      : 'Dismiss for this session'
                  }
                >
                  <X className="w-5 h-5" />
                </button>

                {/* Card content - larger spacing */}
                <div className="p-8">
                  {/* Icon - larger */}
                  <div className={cn(
                    "w-14 h-14 rounded-xl mb-5",
                    "flex items-center justify-center",
                    isPrimary
                      ? "bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
                  )}>
                    <IconComponent className="w-7 h-7" />
                  </div>

                  {/* Title - larger */}
                  <h3 className={cn(
                    "font-serif text-xl lg:text-2xl font-semibold mb-3",
                    "text-slate-800 dark:text-slate-100"
                  )}>
                    {cta.title}
                  </h3>

                  {/* Description - larger */}
                  <p className="text-base text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                    {cta.description}
                  </p>

                  {/* Action button - larger */}
                  {cta.actionLabel && (
                    <Button
                      onClick={cta.action}
                      size="lg"
                      className={cn(
                        isPrimary
                          ? [
                              "bg-teal-600 hover:bg-teal-700",
                              "dark:bg-teal-600 dark:hover:bg-teal-500",
                              "text-white",
                              "shadow-sm hover:shadow-md"
                            ]
                          : [
                              "bg-white dark:bg-slate-800",
                              "border-2 border-slate-200 dark:border-slate-600",
                              "text-slate-700 dark:text-slate-300",
                              "hover:border-teal-600 hover:text-teal-600",
                              "hover:bg-teal-50 dark:hover:bg-teal-950/30",
                              "dark:hover:border-teal-500 dark:hover:text-teal-400"
                            ],
                        "h-12 px-6",
                        "text-base font-semibold",
                        "transition-all duration-200"
                      )}
                    >
                      {cta.actionLabel}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Non-admin guidance card */}
      {!capabilities.canConnectAts &&
        !userContext.hasAtsConnection &&
        !isDismissed('non-admin-guidance') && (
          <div
            className={cn(
              "relative",
              "rounded-xl",
              "border-2 border-cyan-200 dark:border-cyan-800",
              "bg-gradient-to-br from-cyan-50/50 to-white dark:from-cyan-950/20 dark:to-slate-900",
              "p-8",
              "transition-all duration-300",
              dismissingCTAs.includes('non-admin-guidance') && "opacity-0 scale-95"
            )}
          >
            {/* Dismiss button */}
            <button
              onClick={() => handleDismiss('non-admin-guidance', 'permanent')}
              className={cn(
                "absolute top-4 right-4",
                "p-2 rounded-full",
                "text-slate-400 hover:text-slate-600",
                "dark:text-slate-500 dark:hover:text-slate-300",
                "hover:bg-slate-200 dark:hover:bg-slate-700",
                "transition-all duration-200"
              )}
              aria-label="Dismiss"
              title="Don't show this again"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="font-serif text-2xl font-semibold text-slate-800 dark:text-slate-100 mb-4">
              ATS Connection Requires Admin Access
            </h3>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  What you can do now:
                </h4>
                <ul className="space-y-2 text-base text-slate-600 dark:text-slate-400">
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-teal-500 shrink-0" />
                    Add roles and candidates manually
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-teal-500 shrink-0" />
                    Share roles with your network
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-teal-500 shrink-0" />
                    Match candidates to roles
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  Need ATS access?
                </h4>
                <ul className="space-y-2 text-base text-slate-600 dark:text-slate-400 mb-6">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-slate-400 mt-2 shrink-0" />
                    Ask your account administrator to grant you admin permissions
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-slate-400 mt-2 shrink-0" />
                    Or request that they connect your ATS system
                  </li>
                </ul>

                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleContactAdmin}
                    className={cn(
                      "h-12 px-6",
                      "border-2 border-slate-200 dark:border-slate-600",
                      "hover:border-teal-600 hover:text-teal-600",
                      "hover:bg-teal-50 dark:hover:bg-teal-950/30",
                      "text-base font-semibold",
                      "transition-all duration-200"
                    )}
                  >
                    Contact Admin
                  </Button>
                  <button
                    onClick={() => handleDismiss('non-admin-guidance', 'permanent')}
                    className="text-base text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                  >
                    Don't show this again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
